(ns app.bundle-social-tiktok.platform
  "TikTok accounts through bundle.social: a post with one video or one
   image (photo mode), the caption as its text, and the settings of the
   scheduling page's \"TikTok options\" card.

   Only data and pure functions, see `app.bundle-social.platforms` for
   the keys. New TikTok connections go through bundle.social, while the
   accounts connected before keep Storrito's own integration
   (`slices/tiktok-posting`). Both share the scheduling page's TikTok
   group and its options card (`:post-type/destination-group`,
   `:post-type/options-card`). A post of bundle.social is billed as a
   social post.

   TikTok's own creator settings (`creator_info`: the privacy levels a
   creator may choose, disabled comments, duets and stitches) come from
   Storrito's own TikTok token, which an account of bundle.social does
   not have; the card offers every level and setting for it.")

(defn icon
  "Font Awesome's tiktok brand icon."
  [classes]
  [:svg {:xmlns "http://www.w3.org/2000/svg"
         :viewBox "0 0 448 512"
         :fill "currentColor"
         :aria-hidden "true"
         :class classes}
   [:path {:d "M448 209.9a210.1 210.1 0 01-122.8-39.3v178.8A162.6 162.6 0 11185 188.3v89.9a74.6 74.6 0 1052.2 71.2V0h88a121 121 0 00122.8 121.1z"}]])

(def privacy-levels
  "TikTok's privacy levels and their labels, in the order of the options
   card."
  [["PUBLIC_TO_EVERYONE" "Public"]
   ["MUTUAL_FOLLOW_FRIENDS" "Friends"]
   ["FOLLOWER_OF_CREATOR" "Followers"]
   ["SELF_ONLY" "Only me"]])

(def partnership-only-me-error
  "TikTok's content sharing guidelines: a paid partnership cannot be
   seen by the creator only."
  "A TikTok partnership cannot be posted as \"Only me\". Choose another audience.")

(defn options-check
  "The rule across the options: no paid partnership with SELF_ONLY."
  [{:keys [privacy brand-content]}]
  (when (and brand-content (= "SELF_ONLY" privacy))
    partnership-only-me-error))

(defn post-data
  "The `data.TIKTOK` entry of a bundle.social post. Duets and stitches
   only exist for videos, music only for photos."
  [{:keys [media text options upload-id]}]
  (let [video? (not= :image media)]
    (cond-> {:type (if video? "VIDEO" "IMAGE")
             :uploadIds [upload-id]
             :privacy (:privacy options)
             :disableComments (true? (:disable-comments options))
             :isBrandContent (true? (:brand-content options))
             :isOrganicBrandContent (true? (:brand-organic options))
             :isAiGenerated (true? (:ai-generated options))}
      (seq text)
      (assoc :text text)

      video?
      (assoc :disableDuet (true? (:disable-duet options))
             :disableStitch (true? (:disable-stitch options)))

      (and video? (:cover-ms options))
      (assoc :thumbnailOffset (:cover-ms options))

      (not video?)
      (assoc :autoAddMusic (true? (:auto-add-music options))))))

(defn- checkbox
  "A checkbox option of the card."
  [key label]
  {:option/key key
   :option/type :checkbox
   :option/label label})

(def platform
  {:social-platform/id "TIKTOK"
   :social-platform/key :bundle-tiktok
   :social-platform/name "TikTok"
   :social-platform/account-noun "TikTok account"
   :social-platform/icon icon
   :social-platform/text-class ["text-gray-900"]
   :social-platform/pill-class ["bg-gray-50" "border-gray-200" "text-gray-800"]
   :social-platform/launched? false
   ;; The accounts connected before bundle.social, shown and removed on
   ;; the Social Accounts page too.
   :social-platform/own-integration
   {:delete-command :command/tiktok-delete
    :id-key :tiktok.user/id
    :same-ids? false}
   :social-platform/connect-card
   {:eyebrow "Videos & photos"
    :description "Connect a TikTok account for scheduled videos and photo posts."
    :button-text "Connect TikTok"
    :style {:card-class ["border-gray-300" "bg-white" "hover:border-gray-400"]
            :eyebrow-class ["bg-gray-100" "text-gray-700" "ring-gray-200"]
            :icon-class ["bg-black" "text-white" "ring-gray-300"]
            :title-class ["text-gray-900"]
            :description-class ["text-gray-600"]
            :button-class ["bg-black" "text-white" "hover:bg-gray-800"]
            :rail-class ["bg-black"]}}
   :social-platform/post-data post-data
   ;; TikTok takes videos from 3 s to 10 minutes, JPEG or WebP photos and
   ;; captions up to 2,200 characters
   ;; (https://info.bundle.social/api-reference/platforms/tiktok).
   :social-platform/post-types
   [{:post-type/id "POST"
     :post-type/kind :bundle-tiktok-post
     :post-type/label "TikTok post"
     :post-type/group-label "TikTok"
     :post-type/destination-group :tiktok
     :post-type/options-card :tiktok
     :post-type/media #{:video :image}
     :post-type/min-duration-seconds 3
     :post-type/max-duration-seconds 600
     :post-type/caption? true
     :post-type/max-text-length 2200
     :post-type/jpeg-images? true
     :post-type/options-check options-check
     :post-type/options
     [{:option/key :privacy
       :option/label "Who can view this post"
       :option/required? true
       :option/error "Choose who can view this TikTok post."
       :option/choices (mapv (fn [[level label]] {:choice/value level :choice/label label})
                             privacy-levels)}
      (checkbox :disable-comments "Disable comments")
      (checkbox :disable-duet "Disable Duet")
      (checkbox :disable-stitch "Disable Stitch")
      (checkbox :ai-generated "AI Generated")
      (checkbox :brand-content "Partnership")
      (checkbox :brand-organic "Branded Content")
      (checkbox :auto-add-music "Auto Audio")
      {:option/key :cover-ms
       :option/type :number
       :option/label "Cover frame"
       :option/min 0
       ;; TikTok's longest video, 10 minutes, in milliseconds.
       :option/max 600000}]}]})
